/*
 * From https://www.redblobgames.com/grids/voronoi/
 * Copyright 2018 Red Blob Games <redblobgames@gmail.com>
 * License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */
'use strict';


/** like glsl */
function mix(a, b, t) {
    return a * (1.0-t) + b * t;
}
function mixvec2(a, b, t) {
    return [mix(a[0], b[0], t), mix(a[1], b[1], t)];
}

function distancevec2(a, b) {
    let dx = a[0] - b[0],
        dy = a[1] - b[1];
    return Math.sqrt(dx*dx + dy*dy);
}

/**
 * Polar to cartesian
 */
function polarToCartesian(radius, angle) {
    return [radius * Math.cos(angle),
            radius * Math.sin(angle)];
}

/** 
 * Calculate triangle circumcenter from three vertices a,b,c: [x, y]
 * Returns: center is put into 'out' array, and returns 'out' array 
 */
function circumcenter(out, a, b, c) {
    // https://en.wikipedia.org/wiki/Circumscribed_circle#Circumcenter_coordinates
    let ad = a[0]*a[0] + a[1]*a[1],
        bd = b[0]*b[0] + b[1]*b[1],
        cd = c[0]*c[0] + c[1]*c[1];
    let D = 2 * (a[0] * (b[1] - c[1]) + b[0] * (c[1] - a[1]) + c[0] * (a[1] - b[1]));
    out.length = 2;
    out[0] = 1/D * (ad * (b[1] - c[1]) + bd * (c[1] - a[1]) + cd * (a[1] - b[1]));
    out[1] = 1/D * (ad * (c[0] - b[0]) + bd * (a[0] - c[0]) + cd * (b[0] - a[0]));
    return out;
}


/** Calculate triangle incenter and radius from three vertices a,b,c: [x, y]
 *  Returns: center [x, y, radius] is put into 'out' array
 */
function incenter(out, a, b, c) {
    // https://en.wikipedia.org/wiki/Incenter#Cartesian_coordinates
    let aD = distancevec2(b, c),
        bD = distancevec2(a, c),
        cD = distancevec2(a, b);
    let D = aD + bD + cD;
    out.length = 3;
    out[0] = 1/D * (aD * a[0] + bD * b[0] + cD * c[0]);
    out[1] = 1/D * (aD * a[1] + bD * b[1] + cD * c[1]);
    // https://en.wikipedia.org/wiki/Incircle_and_excircles_of_a_triangle#Relation_to_area_of_the_triangle
    let s = D/2;
    let area = Math.sqrt(s * (s-aD) * (s-bD) * (s-cD));
    let radius = area / s;
    out[2] = radius;
    return out;
}

/** 
 * calculate a t_vertex array with alternate centers (pass in the circumcenter or incenter function)
 */
function calculateAlternateCenterPositions(mesh, center) {
    let t_vertex = [], out_r = [];
    for (var t = 0; t < mesh.numSolidTriangles; t++) {
        mesh.t_circulate_r(out_r, t);
        let a = [mesh.r_x(out_r[0]), mesh.r_y(out_r[0])],
            b = [mesh.r_x(out_r[1]), mesh.r_y(out_r[1])],
            c = [mesh.r_x(out_r[2]), mesh.r_y(out_r[2])];
        t_vertex.push(center([], a, b, c));
    }
    for (; t < mesh.numTriangles; t++) {
        t_vertex.push(mesh.t_pos([], t));
    }
    return t_vertex;
}


/** construct a hexagonal grid */
function makeHexGrid(S) {
    let grid = [];
    for (let y = S; y <= 1000-S; y += S) {
        let d = y % (2*S) === 0;
        for (let x = S + (d?(S/2):0); x <= 1000-S; x += S) {
            grid.push([x, y]);
        }
    }
    return grid;
}

/** construct a square grid */
function makeSquareGrid(S) {
    let grid = [];
    for (let y = S; y <= 1000-S; y += S) {
        for (let x = S; x <= 1000-S; x += S) {
            grid.push([x, y]);
        }
    }
    return grid;
}


/** construct a radial grid */
function makeRadialGrid(S) {
    let grid = [];
    for (let r = S; r <= 1500; r += S) {
        let step = Math.PI / (3 + 25*Math.round(r/500));
        for (let a = (r % (2*S) === 0)? step/2 : 0; a < Math.PI/2; a += step) {
            let x = r * Math.cos(a), y = r * Math.sin(a);
            if (x > 1 && y > 1 && x < 999 && y < 999) {
                grid.push([x, y]);
            }
        }
    }
    return grid;
}


module.exports = {
    mix, mixvec2, circumcenter, incenter, calculateAlternateCenterPositions,
    makeHexGrid, makeSquareGrid, makeRadialGrid, polarToCartesian,
};
